import React, { useState, useEffect } from "react";
import { Button, Typography } from "@/design-system/Atoms";
import { useModal } from "@/hooks/useModal";
import LegalContentModal from "@/design-system/Molecules/LegalContentModal";
import { useCheckoutService } from "@/rtkQuery/features/hooks/useCheckoutService";
import { toastService } from "@/utils/toastService";
import type { LegalContent } from "@/design-system/Atoms/Modal/modal.types";
import { TermsAndConditionTemplate } from "@/design-system/Templates";
import { ConfirmationModal, VideoPlayerModal } from "@/design-system/Molecules";
import VideoSectionShimmer from "@/design-system/Organisms/CheckoutConfirmationSection/VideoSectionShimmer";
import type { Video } from "@/rtkQuery/features/models/video.model";
import { AppEmptyState, AppErrorState } from "@/design-system/Feedbacks";

export interface UserInfo {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  phoneNumber?: string;
  dateOfBirth?: Date | string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  FullName?: string;
  DateOfBirth?: Date | string;
  Address?: string;
  City?: string;
  State?: string;
  ZipCode?: string;
  [key: string]: any;
}

interface PaymentInfo {
  paymentMethod?: string;
  cardNumber?: string;
  cardHolderName?: string;
  cardHash?: string;
  status?: string;
  [key: string]: any;
}

interface CheckoutReviewOrderSectionProps {
  onComplete: (reviewData?: any) => void;
  userInfo?: UserInfo;
  paymentInfo?: PaymentInfo;
  className?: string;
  isLoading?: boolean;
}
const getTermsOfServiceContent = (
  customTitle = "Terms of Service"
): LegalContent => ({
  title: customTitle,
  lastUpdated: "March 13th, 2019",
  content: () => <TermsAndConditionTemplate />,
});

const CheckoutReviewOrderSection: React.FC<CheckoutReviewOrderSectionProps> = ({
  onComplete,
  userInfo,
  paymentInfo,
  isLoading = false,
}) => {
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState("");

  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const {
    isOpen: isTermsModalOpen,
    openModal: openTermsModal,
    closeModal: closeTermsModal,
  } = useModal();
  const { submitPayment, submitPaymentErrorMessage } = useCheckoutService();

  const handlePlaceOrder = async () => {
    if (!isTermsAccepted) {
      setTermsError(
        "Please accept the Terms and Conditions before placing the order."
      );
      return;
    }
    setTermsError("");
    setIsPlacingOrder(true);
    try {
      const response = await submitPayment().unwrap();
      toastService.success(response?.Status || "Order placed successfully");

      const reviewData = {
        notes: "Customer approved order for shipment",
        approvedBy: userInfo?.email || "user@example.com",
        isApproved: true,
        shippingMethod: "Expedited 5-7 Business Days",
        orderTimestamp: new Date().toISOString(),
        validationPassed: true,
        orderSummary: {
          customerName:
            userInfo?.FullName ||
            `${userInfo?.firstName} ${userInfo?.lastName}`,
          paymentMethod: paymentInfo?.paymentMethod,
          cardLast4: paymentInfo?.cardNumber?.slice(-4),
          shippingAddress: `${userInfo?.Address || userInfo?.address}, ${userInfo?.City || userInfo?.city}, ${userInfo?.State || userInfo?.state} ${userInfo?.ZipCode || userInfo?.zipCode}`,
        },
        apiResponse: response,
      };

      onComplete(reviewData);
    } catch (error) {
      console.error("Error placing order:", error);
      toastService.error(
        submitPaymentErrorMessage ||
          "There was an error placing your order. Please try again."
      );
    } finally {
      setIsPlacingOrder(false);
    }
  };
  const {
    checkoutReviewData,
    checkoutReviewError,
    checkoutReviewLoading, 
    refetchCheckoutReview,
  } = useCheckoutService();
  const reviewVideos = checkoutReviewData?.Videos ?? [];
  const reviewVideosLoading = checkoutReviewLoading;

  const handleOpenModal = (video: Video) => {
    setSelectedVideo(video);
    setIsVideoModalOpen(true);
  };
  const closeVideoModal = () => {
    setIsVideoModalOpen(false);
    setSelectedVideo(null);
  };

  const allVideosWatched =
    reviewVideos.length > 0 && reviewVideos.every((v) => v.WasWatched);

  const hasVideos = (checkoutReviewData?.Videos?.length ?? 0) > 0;

  useEffect(() => {
    if (allVideosWatched) {
      setIsConfirmationOpen(true);
    }
  }, [allVideosWatched]);

  const canPlaceOrder = !hasVideos || allVideosWatched;

  return (
    <>
      {checkoutReviewError ? (
        <div
          className={`form-bg ${reviewVideos?.length === 0 ? "" : "bg-[#f6f7f9]"} mb-10 rounded-[20px] animate-fade-in`}
        >
          <AppErrorState message={"Failed to fetch Order related videos"} />
        </div>
      ) : (
        checkoutReviewData?.Videos && (
          <>
            {reviewVideosLoading ? (
              <VideoSectionShimmer />
            ) : (
              <>
                <Typography
                  variant="darkBackgroundHeading"
                  as="h2"
                  className="animate-fade-in-up !text-2xl !py-5 mb-6"
                >
                  Attention !
                </Typography>

                <div
                  className={`form-bg ${reviewVideos?.length === 0 ? "" : "bg-[#f6f7f9]"} mb-10 p-8 rounded-[20px] animate-fade-in`}
                >
                  {reviewVideos && reviewVideos?.length === 0 ? (
                    <AppEmptyState message="No videos found." />
                  ) : (
                    <>
                      {reviewVideos && reviewVideos?.length > 0 && (
                        <p className="mb-4">
                          To continue with your order, please watch and confirm
                          all required videos.
                        </p>
                      )}
                      {reviewVideos &&
                        reviewVideos.map((videoItem) => {
                          const { RequiredVideo } = videoItem;
                          return (
                            <div
                              key={videoItem.Id}
                              className="bg-white p-[1.25rem] mb-6 rounded-[8px]"
                            >
                              <div className="flex flex-col sm:flex-row  items-center justify-between gap-4">
                                <div className="flex items-start gap-2">
                                  <input
                                    id={`video-${videoItem.RequiredVideo.Id}`}
                                    type="checkbox"
                                    checked={!!videoItem?.WasWatched}
                                    readOnly
                                    className="mt-[0.35rem] cursor-default w-5 h-5 accent-[var(--color-gray-900)] bg-[var(--color-bg-light)] border border-gray-400 rounded-[3px] appearance-none checked:bg-[var(--color-gray-900)] focus:outline-none focus:ring-4 focus:ring-[rgba(24,26,32,0.25)]"
                                  />
                                  <p className="mb-0 text-[1.25rem] text-[#3B4151]">
                                    {RequiredVideo?.VideoName} -{" "}
                                    {videoItem?.WasWatched
                                      ? "Confirmed."
                                      : "Please Watch & Confirm."}
                                  </p>
                                </div>
                                <Button
                                  variant="secondary"
                                  size="xl"
                                  onClick={() =>
                                    handleOpenModal({
                                      Id: RequiredVideo.Id,
                                      ProductId: RequiredVideo.ProductId,
                                      SortOrder: RequiredVideo.SortOrder,
                                      Url: RequiredVideo.Url,
                                      VideoName: RequiredVideo.VideoName,
                                      FileName: RequiredVideo.FileName,
                                      Thumbnail: RequiredVideo.Thumbnail,
                                      IsHidden: RequiredVideo.IsHidden,
                                      RecordCreatedDateTime:
                                        new Date().toISOString(),
                                      RecordModifiedDateTime: null,
                                      IsRemoved: 0,
                                      RemovedAt: null,
                                      WasWatched: videoItem.WasWatched,
                                    })
                                  }
                                >
                                  View Video
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                    </>
                  )}
                </div>
              </>
            )}
          </>
        )
      )}

      <div className="form-bg bg-[#f6f7f9] mb-6 p-8 rounded-[20px]">
        <h3 className="text-2xl font-normal mb-5 text-[#181a20]">Shipping</h3>

        <div className="bg-white p-5 mb-6 rounded-lg">
          <div className="flex flex-col gap-3">
            <p className="text-[1.25rem] font-normal mb-[0.5rem] text-[#3B4151]">
              Expedited 5-7 Business Days
            </p>

            <div className="flex items-start gap-2">
              <input
                id="terms"
                type="checkbox"
                checked={isTermsAccepted}
                onChange={(e) => {
                  setIsTermsAccepted(e.target.checked);
                  if (e.target.checked) setTermsError("");
                }}
                className="cursor-pointer w-5 h-5 accent-[var(--color-gray-900)] bg-[var(--color-bg-light)] border border-gray-400 rounded-[3px] appearance-none checked:bg-[var(--color-gray-900)] focus:outline-none focus:ring-4 focus:ring-[rgba(24,26,32,0.25)]"
              />
              <label
                htmlFor="terms"
                className="text-[1rem] font-normal text-[#3B4151] select-none cursor-pointer"
              >
                By placing order you agree to Defy Medical's{" "}
                <button
                  onClick={openTermsModal}
                  className="transition-colors duration-200 bg-transparent border-none p-0 m-0 cursor-pointer"
                  type="button"
                >
                  <strong className="text-[#181a20] underline">
                    Terms and Conditions
                  </strong>
                </button>
              </label>
            </div>
            {termsError && (
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-600 text-sm">{termsError}</p>
              </div>
            )}
          </div>
        </div>

        <Button
          variant="secondary"
          onClick={handlePlaceOrder}
          size="xl"
          fullWidth
          disabled={isLoading || isPlacingOrder || !canPlaceOrder}
        >
          {isPlacingOrder ? (
            <span className="flex items-center justify-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Processing Order...
            </span>
          ) : (
            "Place Order"
          )}
        </Button>

        <LegalContentModal
          isOpen={isTermsModalOpen}
          onClose={closeTermsModal}
          content={getTermsOfServiceContent("Terms & Conditions")}
        />
      </div>
      {selectedVideo && (
        <VideoPlayerModal
          isOpen={isVideoModalOpen}
          onClose={closeVideoModal}
          content={selectedVideo}
          onAcknowledged={async () => { 
            await refetchCheckoutReview();
          }}
        />
      )}
      <ConfirmationModal
        isOpen={isConfirmationOpen}
        onClose={() => setIsConfirmationOpen(false)}
      />
    </>
  );
};

export default CheckoutReviewOrderSection;
